import * as Yup from 'yup';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Autocomplete,
  Button,
  Divider,
  Link,
  Table,
  TableBody,
  TableContainer,
  TextField,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getPaginatedEmployees } from 'src/redux/slices/employees';
import { getPaginatedUsers, IListUser, UserStatus } from 'src/redux/slices/users';
import {
  createPackagingReturn,
  TCreatePackagingReturn,
  updatePackagingReturn,
} from 'src/redux/slices/packaging-return';

import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useSnackbar as useAppSnackbar } from 'src/components/snackbar';
import CustomDatePicker from 'src/components/custom-datepicker/custom-datepicker';

import { EmployeeStatus } from 'src/types/employee';
import { IPackagingReturnDetailedItem } from 'src/types/packaging-return';
import { IPackagingDispatchItemSearchResult } from 'src/types/packaging-dispatch';

import { paths } from '../../routes/paths';
import PackagingReturnNewItemRow from './packaging-return-new-item-row';

type Props = {
  currentReturn?: IPackagingReturnDetailedItem;
};

export default function PackagingReturnNewEditForm({ currentReturn }: Props) {
  const dispatch = useAppDispatcher();
  const { enqueueSnackbar } = useAppSnackbar();
  const navigate = useNavigate();

  // The damaged-quantity split (and its auto-created linked damage
  // dispatch) can only ever be introduced on create — the backend refuses
  // to edit a return that has, or would newly have, damaged lines, since
  // the linked damage dispatch has no diffing/re-sync logic. So the whole
  // toggle/split UI is hidden in edit mode rather than shown and rejected.
  const isCreateMode = !currentReturn;

  const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
    { id: 'itemNo', label: '#', align: 'left', primary: true },
    { id: 'sourceDispatchItem', label: 'Source Dispatch Item', align: 'left', primary: true },
    { id: 'quantity', label: 'Quantity', align: 'left', primary: true },
    { id: 'issuedBy', label: 'Issued By', align: 'left', primary: true },
    ...(isCreateMode
      ? [{ id: 'damaged', label: 'Damaged', align: 'left' as const, primary: true }]
      : []),
    { id: '', primary: false },
  ];

  const { users } = useAppSelector((state) => state.users);

  const [userSearchQuery, setUserSearchQuery] = useState('');

  const NewPackagingReturnSchema = Yup.object().shape({
    receivedBy: Yup.string().required('Received by is required'),
    returnDate: Yup.date().typeError('Invalid date').required('Return date is required'),
    remarks: Yup.string().nullable(),
    items: Yup.array()
      .of(
        Yup.object().shape({
          sourceDispatchItem: Yup.string().required('Source dispatch item is required'),
          quantity: Yup.number()
            .typeError('')
            .moreThan(0, 'Quantity must be greater than zero')
            .required('Quantity is required'),
          issuedBy: Yup.string().required('Issued by is required'),
          damaged: Yup.boolean(),
          supplierDamagedQuantity: Yup.number().typeError('').min(0),
          staffDamagedQuantity: Yup.number().typeError('').min(0),
        }).test(
          'valid-damaged-quantity',
          'Damaged quantity must be greater than zero and cannot exceed the line quantity',
          (item) => {
            if (!item?.damaged) return true;
            const damagedQuantity =
              (Number(item.supplierDamagedQuantity) || 0) +
              (Number(item.staffDamagedQuantity) || 0);
            return damagedQuantity > 0 && damagedQuantity <= (Number(item.quantity) || 0);
          }
        )
      )
      .min(1, 'At least one item is required')
      .required('Items are required'),
  });

  const defaultItem = useMemo(
    () => ({
      sourceDispatchItem: '',
      quantity: 1,
      issuedBy: '',
      damaged: false,
      supplierDamagedQuantity: 0,
      staffDamagedQuantity: 0,
    }),
    []
  );

  const defaultValues = useMemo(
    () => ({
      receivedBy: currentReturn?.receivedBy._id ?? '',
      returnDate: currentReturn?.returnDate ? new Date(currentReturn.returnDate) : new Date(),
      remarks: currentReturn?.remarks ?? null,
      items: currentReturn?.items.map((item) => ({
        sourceDispatchItem: item.sourceDispatchItem._id,
        quantity: item.quantity,
        issuedBy: item.issuedBy?._id ?? '',
        damaged: item.damaged ?? false,
        supplierDamagedQuantity: item.supplierDamagedQuantity ?? 0,
        staffDamagedQuantity: item.staffDamagedQuantity ?? 0,
      })) ?? [defaultItem],
    }),
    [currentReturn, defaultItem]
  );

  const methods = useForm({
    resolver: yupResolver(NewPackagingReturnSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting, errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  // Consecutive lines are very often the same returning employee, so a
  // newly added line (not the first) starts pre-filled with whatever the
  // previous line's issuedBy was — free to change, just saves a re-pick.
  const handleAddItem = () => {
    const items = getValues('items');
    const lastIssuedBy = items[items.length - 1]?.issuedBy ?? '';
    append({ ...defaultItem, issuedBy: lastIssuedBy });
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      const payload: TCreatePackagingReturn = {
        ...data,
        remarks: data.remarks ?? null,
      };

      if (currentReturn) {
        await dispatch(
          updatePackagingReturn({
            returnId: currentReturn._id,
            returnTransaction: payload,
          })
        ).unwrap();

        enqueueSnackbar('Return updated');
      } else {
        await dispatch(createPackagingReturn({ returnTransaction: payload })).unwrap();

        enqueueSnackbar('Return created');
      }

      navigate(paths.dashboard.packagingReturn.root);
      reset();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message, {
          variant: 'error',
        });
      }
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaginatedUsers = useCallback(
    debounce((query) => {
      dispatch(
        getPaginatedUsers({
          page: 0,
          limit: 25,
          firstName: query,
          email: null,
          lastName: null,
          permissions: [],
          sort: 'asc',
          sortBy: 'firstName',
          status: [UserStatus.ACTIVE],
        })
      );
    }, 500),
    [dispatch]
  );

  useEffect(() => {
    fetchPaginatedUsers(userSearchQuery);

    return fetchPaginatedUsers.cancel;
  }, [userSearchQuery, fetchPaginatedUsers]);

  // Fetched once, unfiltered, and filtered/searched client-side per row
  // instead of a shared debounced server search, since issuedBy now lives
  // on every row instead of the header, and each row needs to search
  // independently without stepping on the others.
  useEffect(() => {
    dispatch(
      getPaginatedEmployees({
        page: null,
        limit: null,
        generalSearch: null,
        sortBy: 'firstName',
        sort: 'asc',
        status: EmployeeStatus.ACTIVE,
      })
    );
  }, [dispatch]);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle2">Return Information</Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="returnDate"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CustomDatePicker
                      disableFuture
                      label="Return Date"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} md={6} />

              <Grid xs={12} md={6}>
                <Controller
                  name="receivedBy"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      {...field}
                      fullWidth
                      multiple={false}
                      options={users.users}
                      isOptionEqualToValue={(
                        option: IListUser | string,
                        value: IListUser | string
                      ) => {
                        const optionId = typeof option === 'string' ? option : option?._id;
                        const valueId = typeof value === 'string' ? value : value?._id;
                        return optionId === valueId;
                      }}
                      getOptionLabel={(option: IListUser | string) => {
                        if (!option) return '';
                        if (typeof option === 'string') {
                          if (option.length === 0) return option;
                          const value = users.users.find((user: IListUser) => user._id === option);
                          return value
                            ? `${value.firstName}${value.lastName ? ` ${value.lastName}` : ''}`
                            : '';
                        }
                        return `${option.firstName}${option.lastName ? ` ${option.lastName}` : ''} (${option.email})`;
                      }}
                      renderOption={(props, option: IListUser | string) => {
                        if (typeof option === 'string') {
                          return (
                            <li {...props} key={option}>
                              {option}
                            </li>
                          );
                        }
                        return (
                          <li {...props} key={option._id}>
                            {option.firstName}
                            {option.lastName ? ` ${option.lastName}` : ''} ({option.email})
                          </li>
                        );
                      }}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') setUserSearchQuery(value);
                        if (reason === 'clear') setUserSearchQuery('');
                      }}
                      renderInput={(params) => (
                        <TextField
                          label="Received By (Staff)"
                          required
                          error={!!error}
                          helperText={error?.message}
                          {...params}
                        />
                      )}
                      onChange={(event, newValue) => {
                        field.onChange(typeof newValue === 'string' ? newValue : newValue?._id ?? '');
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12}>
                <RHFTextField name="remarks" label="Remarks" multiline rows={2} />
              </Grid>

              <Divider
                sx={{
                  width: 1,
                  my: 2,
                  borderStyle: 'dashed',
                }}
              />

              <Grid mb={1} xs={12} width="full">
                <Typography variant="subtitle2">Line Items</Typography>

                {typeof errors.items?.message === 'string' && (
                  <Typography variant="caption" color="error.main" component="p" mt={0.5}>
                    {errors.items.message}
                  </Typography>
                )}
              </Grid>

              <Grid xs={12}>
                <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '300px' }}>
                  <Scrollbar>
                    <Table stickyHeader size="small" sx={{ minWidth: 780 }}>
                      <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                      <TableBody>
                        {fields.map((item, index) => (
                          <PackagingReturnNewItemRow
                            key={item.id}
                            index={index}
                            remove={remove}
                            isLast={fields.length === 1 && index === 0}
                            excludeReturnId={currentReturn?._id}
                            allowDamaged={isCreateMode}
                            initialDispatchItem={
                              currentReturn?.items[index]
                                ? ({
                                    _id: currentReturn.items[index].sourceDispatchItem._id,
                                    dispatch: currentReturn.items[index].sourceDispatchItem.dispatch,
                                    packagingItem: currentReturn.items[index].packagingItem,
                                    product: currentReturn.items[index].product,
                                    quantity: currentReturn.items[index].sourceDispatchItem.quantity,
                                  } as IPackagingDispatchItemSearchResult)
                                : undefined
                            }
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </Scrollbar>
                </TableContainer>

                <Stack mt={2}>
                  <Button variant="soft" color="primary" onClick={handleAddItem}>
                    Add
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" justifyContent="space-between" gap={2} mt={4}>
              <Link component={RouterLink} href={paths.dashboard.packagingReturn.root}>
                <Button variant="outlined" color="inherit">
                  Cancel
                </Button>
              </Link>

              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                {!currentReturn ? 'Create Return' : 'Save Changes'}
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
